import bindCommands from "Areas/Documents/graph/utils/commands/bindCommands";
import configureInputModes from "Areas/Documents/graph/utils/configure/configureInputModes";
import undoEngine from "Areas/Documents/graph/utils/configure/undoEngine";
import updateOrthogonalEdgeEditingContext from "Areas/Documents/graph/utils/configure/updateOrthogonalEdgeEditingContext";
import layoutAlgorithmMap from "Areas/Documents/graph/utils/layoutAlgorithms/layoutAlgorithmMap";
import { BrowserDetection } from "Common/browser/browserDetection";
import useBeforeUnloadMessage from "Common/componentUtils/useBeforeUnloadMessage";
import useKeyboardEventListener from "Common/componentUtils/useKeyboardEventListener";
import { ROUTE_ANCHOR } from "Common/routingUtils";
import ProgressOverlay from "Components/progressOverlay";
import { themeVariables } from "Components/theme";
import YesNoConfirmation from "Components/yesNoConfirmation";
import yFilesLicense from "Content/vendor/yWorks/yWorks.yFilesHTML.License.json";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import filter from "lodash/filter";
import includes from "lodash/includes";
import map from "lodash/map";
import pick from "lodash/pick";
import size from "lodash/size";
import trim from "lodash/trim";
import values from "lodash/values";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { GraphBuilder, GraphItemTypes, License, Point, VisualCachingPolicy, YGraphAdapter } from "yfiles";
import { GRAPH_IN_PROGRESS_CLASS_NAME, ONTOLOGICAL_MODEL_BG, ZOOM } from "./constants";
import locale from "./ontologicalModel.locale";
import RightSideDetailsPanel from "./rightSideDetailsPanel";
import RowActionsPanel from "./rowActionsPanel";
import buildDeleteDocumentObjectById from "./utils/buildCallbacks/buildDeleteDocumentObjectById";
import buildDeleteSelectedCallback from "./utils/buildCallbacks/buildDeleteSelectedCallback";
import buildOnDocumentObjectAddCallback from "./utils/buildCallbacks/buildOnDocumentObjectAddCallback";
import buildOnNodeAttributeAddCallback from "./utils/buildCallbacks/buildOnNodeAttributeAddCallback";
import buildOnNodeAttributesSortCallback from "./utils/buildCallbacks/buildOnNodeAttributesSortCallback";
import buildOnNodeDataUpdateCallback from "./utils/buildCallbacks/buildOnNodeDataUpdateCallback";
import createGraphComponent from "./utils/createGraphComponent";
import filterGraphByText from "./utils/filterGraphByText";
import generateNodeAttributeId from "./utils/generateNodeAttributeId";
import addGraphListeners from "./utils/listeners/addGraphListeners";
import buildModelErrors from "./utils/modelErrors/buildModelErrors";
import initializeDefaultStyles from "./utils/styling/initializeDefaultStyles";
import initializeShowNamesNodeStyles from "./utils/styling/initializeShowNamesNodeStyles";
import applyData from "./utils/updates/applyData";
import { createProviderWebsocketProvider } from "Common/api/wsServer";
import { Doc } from "yjs";
import { RemoteCursorsProvider, RemoteCursorsGraph } from "Components/RemoteCursors";
License.value = yFilesLicense;
const SIZES = {
    ROW_ACTIONS_PANEL_HEIGHT: 52,
    CREATE_NODE_BTN_HEIGHT: 36
};
const getLinkHref = (target) => {
    if (!target)
        return undefined;
    const parentLink = target.closest(`a[href^="${ROUTE_ANCHOR}"]`);
    return parentLink === null || parentLink === void 0 ? void 0 : parentLink.getAttribute("href");
};
export default function OntologicalModel(props) {
    var _a, _b;
    const divRef = useRef(null);
    const dataLoadedRef = useRef(Boolean(props.data));
    const graphComponentRef = useRef();
    const hasChangesRef = useRef(Boolean(props.hasChanges));
    const initialHasChangesRef = useRef(Boolean(props.hasChanges));
    const timerRef = useRef(null);
    const [selected, setSelected] = useState({
        nodeIds: [],
        edgeIds: [],
        fromNode: undefined,
        toNode: undefined
    });
    const [sourceModelWasApplied, setSourceModelWasApplied] = useState(false);
    const graphModelAppliedRef = useRef({
        readyForListeners: false
    });
    const [renderingManager, setRenderingManager] = useState(undefined);
    const [zoom, setZoom] = useState(ZOOM.DEFAULT);
    const layoutAlgorithmRef = useRef(props.layoutAlgorithm);
    const layoutAlgorithmTimerRef = useRef(null);
    const [inProgress, setInProgress] = useState(false);
    const { height, ref: observableRef } = useResizeDetector();
    const canEditRef = useRef(Boolean(props.canEdit));
    const showNamesRef = useRef(Boolean(props.showNames));
    const graphAdapterRef = useRef();
    const changeNodeNameTimersRef = useRef({});
    const [modelErrors, setModelErrors] = useState([]);
    const goAwayConfirmationPopoverRef = useRef(null);
    const linkHrefRef = useRef(null);
    const [provider, setProvider] = useState(null);
    const [graphComponent, setGraphComponent] = useState(null);
    useEffect(() => {
        canEditRef.current = Boolean(props.canEdit);
    }, [Boolean(props.canEdit)]);
    useEffect(() => {
        if (sourceModelWasApplied) {
            canEditRef.current && undoEngine(graphComponentRef);
            graphModelAppliedRef.current.readyForListeners = true;
        }
    }, [sourceModelWasApplied]);
    useEffect(() => {
        showNamesRef.current = Boolean(props.showNames);
    }, [Boolean(props.showNames)]);
    useEffect(() => {
        hasChangesRef.current = Boolean(props.hasChanges);
    }, [props.hasChanges]);
    useEffect(() => {
        dataLoadedRef.current = Boolean(props.data);
    }, [Boolean(props.data)]);
    const handleDeleteSelected = useCallback(() => {
        if (!props.canEdit || !selected.nodeIds.length || !renderingManager || inProgress)
            return;
        buildDeleteSelectedCallback(graphComponentRef, renderingManager)();
    }, [Boolean(selected.nodeIds.length), renderingManager, inProgress]);
    const handleDeleteDocumentObjectById = useCallback((documentObjectId) => {
        if (!renderingManager)
            return;
        buildDeleteDocumentObjectById(graphComponentRef, renderingManager)(documentObjectId);
    }, [renderingManager]);
    const handleChangeSelected = useCallback((selectedNodes, selectedEdges) => {
        var _a, _b, _c, _d;
        if (selectedNodes.length) {
            setSelected({
                nodeIds: compact(map(selectedNodes, "tag.Id")),
                edgeIds: [],
                fromNode: undefined,
                toNode: undefined
            });
        }
        else if (selectedEdges.length) {
            setSelected({
                nodeIds: [],
                edgeIds: compact(map(selectedEdges, "tag.Id")),
                fromNode: selectedEdges.length === 1 ? (_b = (_a = selectedEdges[0].sourceNode) === null || _a === void 0 ? void 0 : _a.tag) === null || _b === void 0 ? void 0 : _b.Id : undefined,
                toNode: selectedEdges.length === 1 ? (_d = (_c = selectedEdges[0].targetNode) === null || _c === void 0 ? void 0 : _c.tag) === null || _d === void 0 ? void 0 : _d.Id : undefined
            });
        }
        else {
            setSelected({ nodeIds: [], edgeIds: [], fromNode: undefined, toNode: undefined });
        }
    }, []);
    useKeyboardEventListener("Delete", false, handleDeleteSelected);
    useBeforeUnloadMessage(hasChangesRef);
    const handleChangeZoom = useCallback((newZoom) => {
        setZoom(newZoom);
    }, []);
    const setGraphInProgress = useCallback((val) => {
        var _a;
        if (!((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected))
            return;
        setInProgress(prev => {
            var _a;
            if (prev === val || !((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.div))
                return prev;
            const divClassList = graphComponentRef.current.div.classList;
            if (val) {
                divClassList.add(GRAPH_IN_PROGRESS_CLASS_NAME);
            }
            else {
                divClassList.remove(GRAPH_IN_PROGRESS_CLASS_NAME);
            }
            return val;
        });
    }, []);
    useEffect(() => {
        var _a;
        if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.div) || sourceModelWasApplied !== true)
            return;
        const inputMode = graphComponentRef.current.inputMode;
        if (!inputMode)
            return;
        inputMode.movableItems = inProgress
            ? GraphItemTypes.NONE
            : GraphItemTypes.NODE | GraphItemTypes.BEND | GraphItemTypes.EDGE;
        inputMode.moveInputMode.enabled = !inProgress;
    }, [inProgress, props.canEdit, sourceModelWasApplied]);
    useEffect(() => {
        if (!graphComponentRef.current &&
            divRef.current &&
            divRef.current.isConnected &&
            !divRef.current.hasChildNodes()) {
            if (!BrowserDetection.webGL2) {
                props.showError && props.showError(locale.ERROR_WHILE_WEB_GL2_GRAPH_CONFIGURATION);
                return;
            }
            setGraphInProgress(true);
            graphComponentRef.current = createGraphComponent();
            setGraphComponent(graphComponentRef.current);
            graphComponentRef.current.visualCaching = VisualCachingPolicy.ALWAYS;
            const graphBuilder = new GraphBuilder(graphComponentRef.current.graph);
            graphComponentRef.current.graph = graphBuilder.buildGraph();
            graphComponentRef.current.graph.undoEngineEnabled = false;
            bindCommands(graphComponentRef, props.undoSelector, props.redoSelector);
            updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
            const inputMode = configureInputModes(graphComponentRef, Boolean(props.canEdit), true);
            if (inputMode) {
                inputMode.deletableItems = GraphItemTypes.NONE;
                inputMode.allowCreateEdge = false;
                inputMode.createEdgeInputMode.enabled = false;
                inputMode.toolTipItems = GraphItemTypes.NODE;
            }
            addGraphListeners(graphComponentRef, inputMode, graphModelAppliedRef, layoutAlgorithmRef, props.onChangeModel, handleChangeZoom, handleChangeSelected);
            initializeDefaultStyles(graphComponentRef.current, showNamesRef).then((rManager) => {
                if (!graphComponentRef.current || !divRef.current || !rManager)
                    return;
                rManager === null || rManager === void 0 ? void 0 : rManager.registerListeners();
                divRef.current.appendChild(graphComponentRef.current.div);
                graphComponentRef.current.div.classList.add("invisible");
                if (!dataLoadedRef.current)
                    setGraphInProgress(false);
                setRenderingManager(rManager);
            });
        }
    }, []);
    useEffect(() => {
        var _a;
        if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) || !renderingManager)
            return;
        renderingManager.setWebGL2Styles(true);
        graphComponentRef.current.updateVisual();
    }, [props.showNames]);
    useEffect(() => {
        if (graphComponentRef.current && graphComponentRef.current.zoom !== zoom) {
            graphComponentRef.current.zoom = zoom;
        }
    }, [zoom]);
    const filterText = useMemo(() => trim(props.filterText), [props.filterText]);
    const filterGraphCallback = useCallback((text, rManager) => {
        if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
            return;
        if (!graphAdapterRef.current && !text)
            return;
        setGraphInProgress(true);
        if (!graphAdapterRef.current) {
            graphAdapterRef.current = new YGraphAdapter(graphComponentRef.current.graph);
        }
        filterGraphByText(graphComponentRef, graphAdapterRef, text);
        rManager.setWebGL2Styles(true);
        if (!text) {
            graphAdapterRef.current = undefined;
        }
        setGraphInProgress(false);
    }, []);
    useEffect(() => {
        if (!sourceModelWasApplied || (inProgress && !graphAdapterRef.current) || !renderingManager)
            return;
        filterGraphCallback(filterText, renderingManager);
    }, [filterText, inProgress, renderingManager]);
    const handleChangeLayoutAlgorithm = useCallback((newLayoutAlgorithm) => {
        if (graphComponentRef.current &&
            newLayoutAlgorithm &&
            layoutAlgorithmMap[newLayoutAlgorithm]) {
            setGraphInProgress(true);
            props.onSetLayoutAlgorithm && props.onSetLayoutAlgorithm(newLayoutAlgorithm);
            layoutAlgorithmRef.current = newLayoutAlgorithm;
            updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
            const config = layoutAlgorithmMap[layoutAlgorithmRef.current]();
            layoutAlgorithmTimerRef.current && clearTimeout(layoutAlgorithmTimerRef.current);
            layoutAlgorithmTimerRef.current = setTimeout(() => {
                var _a;
                if (!graphComponentRef.current || !((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected))
                    return;
                config.apply(graphComponentRef.current, () => {
                    setGraphInProgress(false);
                    updateOrthogonalEdgeEditingContext(graphComponentRef, layoutAlgorithmRef);
                }, () => {
                    props.showError && props.showError(locale.ERROR_WHILE_APPLYING_LAYOUT);
                });
            });
        }
    }, [props.onSetLayoutAlgorithm]);
    const savingCallback = useCallback(() => {
        var _a;
        if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) ||
            !props.canEdit ||
            !sourceModelWasApplied ||
            !props.data ||
            !props.onSaveData)
            return;
        const documentObjects = props.data.DocumentObjects;
        const transforms = props.data.Transforms;
        props.onSaveData(documentObjects, () => {
            var _a;
            props.onChangeModel &&
                props.onChangeModel({
                    transforms: values(transforms),
                    documentObjects: values(documentObjects),
                    onlyDiffSources: false
                }, true);
            if ((_a = divRef.current) === null || _a === void 0 ? void 0 : _a.isConnected)
                initialHasChangesRef.current = false;
        });
    }, [sourceModelWasApplied, props.canEdit, props.data]);
    const handlePreventEvent = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!goAwayConfirmationPopoverRef.current)
            return;
        goAwayConfirmationPopoverRef.current.instance.show("body");
    }, []);
    useEffect(() => {
        const callback = (event) => {
            var _a;
            if (!hasChangesRef.current || !goAwayConfirmationPopoverRef.current)
                return;
            const { target, relatedTarget } = event;
            getLinkHref(relatedTarget) && (relatedTarget === null || relatedTarget === void 0 ? void 0 : relatedTarget.removeEventListener("click", handlePreventEvent));
            if ((_a = goAwayConfirmationPopoverRef.current) === null || _a === void 0 ? void 0 : _a.instance.option("visible"))
                return;
            linkHrefRef.current = getLinkHref(target);
            if (linkHrefRef.current) {
                target === null || target === void 0 ? void 0 : target.addEventListener("click", handlePreventEvent);
                event.stopPropagation();
            }
        };
        window.addEventListener("mouseover", callback);
        return () => {
            window.removeEventListener("mouseover", callback);
        };
    }, []);
    useEffect(() => {
        props.onInitialized && props.onInitialized();
        return () => {
            timerRef.current && clearTimeout(timerRef.current);
            layoutAlgorithmTimerRef.current && clearTimeout(layoutAlgorithmTimerRef.current);
        };
    }, []);
    useEffect(() => {
        if (graphComponentRef.current &&
            props.data &&
            !props.saveInProgress &&
            !sourceModelWasApplied &&
            renderingManager) {
            setGraphInProgress(true);
            initialHasChangesRef.current = Boolean(props.hasChanges);
            const { data } = props;
            graphComponentRef.current.graph.clear();
            graphComponentRef.current.zoom = ZOOM.DEFAULT;
            graphComponentRef.current.viewPoint = new Point(0, 0);
            graphComponentRef.current.updateContentRect();
            if (size(data.DocumentObjects)) {
                initializeShowNamesNodeStyles(values(data.DocumentObjects))
                    .then(() => {
                    if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
                        return;
                    applyData(graphComponentRef, data, layoutAlgorithmRef, setGraphInProgress, () => {
                        renderingManager.setWebGL2Styles(true);
                    }, () => {
                        var _a;
                        if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
                            return;
                        (_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.fitGraphBounds();
                        filterGraphCallback(filterText, renderingManager);
                        setSourceModelWasApplied(true);
                        graphComponentRef.current.div.classList.remove("invisible");
                    }, () => {
                        if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
                            return;
                        props.showError && props.showError(locale.ERROR_WHILE_LOAD_GRAPH);
                        graphComponentRef.current.div.classList.remove("invisible");
                    });
                })
                    .catch(ex => {
                    props.showError && props.showError(ex);
                });
            }
            else {
                setSourceModelWasApplied(true);
                setGraphInProgress(false);
            }
        }
    }, [props.data, props.saveInProgress, sourceModelWasApplied, renderingManager]);
    const handleDataUpdate = useCallback(buildOnNodeDataUpdateCallback(graphComponentRef, renderingManager, showNamesRef, changeNodeNameTimersRef), [renderingManager]);
    const handleGenerateNodeAttributeId = useCallback(() => generateNodeAttributeId(graphComponentRef), []);
    const handleAttributeAdd = useCallback(buildOnNodeAttributeAddCallback(graphComponentRef), []);
    const handleAttributesSort = useCallback(buildOnNodeAttributesSortCallback(graphComponentRef), []);
    const handleDocumentObjectAdd = useCallback(buildOnDocumentObjectAddCallback(graphComponentRef, layoutAlgorithmRef, renderingManager, setGraphInProgress), [renderingManager]);
    const detailsPanelOpen = useMemo(() => props.detailsPanelOpenByInteraction === undefined
        ? Boolean(selected.nodeIds.length + selected.edgeIds.length)
        : props.detailsPanelOpenByInteraction, [props.detailsPanelOpenByInteraction, selected]);
    const detailsPanelData = useMemo(() => {
        var _a, _b;
        if (selected.nodeIds.length && ((_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObjects)) {
            return values(pick(props.data.DocumentObjects, selected.nodeIds));
        }
        if (selected.edgeIds.length && ((_b = props.data) === null || _b === void 0 ? void 0 : _b.Transforms)) {
            return filter(values(props.data.Transforms), tr => includes(selected.edgeIds, tr.Id));
        }
        return [];
    }, [selected.nodeIds, selected.edgeIds, props.data]);
    const detailsPanelDataFrom = useMemo(() => {
        var _a;
        return selected.fromNode && ((_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObjects)
            ? props.data.DocumentObjects[selected.fromNode]
            : undefined;
    }, [selected.fromNode, (_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObjects]);
    const detailsPanelDataTo = useMemo(() => {
        var _a;
        return selected.toNode && ((_a = props.data) === null || _a === void 0 ? void 0 : _a.DocumentObjects)
            ? props.data.DocumentObjects[selected.toNode]
            : undefined;
    }, [selected.toNode, (_b = props.data) === null || _b === void 0 ? void 0 : _b.DocumentObjects]);
    const dataChangedCallback = useCallback(debounce((data) => {
        setModelErrors(buildModelErrors(data));
    }, 800), []);
    useEffect(() => {
        dataChangedCallback(props.data);
    }, [props.data]);
    const handleGoAwayConfirm = useCallback(() => {
        if (linkHrefRef.current)
            window.location.href = linkHrefRef.current;
    }, []);
    useEffect(() => {
        const doc = new Doc();
        const roomName = "graph_ontologicalModel";
        const p = createProviderWebsocketProvider(roomName, doc);
        setProvider(p);
        return () => p === null || p === void 0 ? void 0 : p.destroy();
    }, []);
    return (React.createElement(ProgressOverlay, { show: props.inProgress || props.saveInProgress || inProgress, className: props.className, zIndex: parseInt(themeVariables["zindex-devextreme-dx-menu-base"]) + 1 },
        React.createElement("div", { className: "position-relative overflow-hidden", ref: observableRef, style: ONTOLOGICAL_MODEL_BG },
            React.createElement(RowActionsPanel, { canUndoOrRedo: props.canEdit, rightSidePanel: React.createElement(RightSideDetailsPanel, { closedHeight: SIZES.ROW_ACTIONS_PANEL_HEIGHT, openedHeight: height ? height - SIZES.ROW_ACTIONS_PANEL_HEIGHT : height, onDataUpdate: props.canEdit ? handleDataUpdate : undefined, canModifyAttributes: props.canEdit, onDeleteDocumentObject: props.canEdit ? handleDeleteDocumentObjectById : undefined, onAttributeAdd: props.canEdit ? handleAttributeAdd : undefined, generateAttributeId: handleGenerateNodeAttributeId, onAttributesSort: props.canEdit ? handleAttributesSort : undefined, open: detailsPanelOpen, onChangeOpen: props.onChangeDetailedPanelOpen, data: detailsPanelData, transformFrom: detailsPanelDataFrom, transformTo: detailsPanelDataTo }), rowActionsPanelHeight: SIZES.ROW_ACTIONS_PANEL_HEIGHT, zoom: zoom, onChangeZoom: handleChangeZoom, layoutAlgorithm: props.layoutAlgorithm, onChangeLayoutAlgorithm: handleChangeLayoutAlgorithm, onSave: props.hasChanges && props.canEdit ? savingCallback : undefined, onDelete: props.canEdit && Boolean(selected.nodeIds.length + selected.edgeIds.length)
                    ? handleDeleteSelected
                    : undefined, handleDocumentObjectAdd: handleDocumentObjectAdd, modelErrors: modelErrors, sourceModelWasApplied: sourceModelWasApplied }),
            React.createElement("div", { style: { position: "relative" }, className: "stretched-to-fit" },
                React.createElement("div", { ref: divRef, className: "stretched-to-fit" }),
                React.createElement(RemoteCursorsProvider, { provider: provider },
                    React.createElement(RemoteCursorsGraph, { graph: graphComponent }))),
            React.createElement(YesNoConfirmation, { popoverRef: goAwayConfirmationPopoverRef, title: locale.HAS_UNSAVED_DATA, headerTitle: locale.CONFIRM_EXIT, onConfirm: handleGoAwayConfirm, hideOnOutsideClick: false, hideOnParentScroll: false, width: 400 }))));
}
